import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Heading from '@/components/atoms/Heading';
import Media from '@/components/atoms/Media';
import SectionWrapper from '@/components/atoms/SectionWrapper';
import { emphasiseText } from '@/helpers/emphasiseText';
import { useInsetReveal } from '@/hooks/animations/use-inset-reveal';
import { getComponentPadding } from '@/utils/get-component-padding';
import { isHeadlessBrowser } from '@/utils/isHeadlessBrowser';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import Breadcrumb from '../../molecules/Breadcrumb';
gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);
const ArticleHero = ({ componentID, theme, background, topPadding, bottomPadding, category, articleDate, title, text, author, authorTitle, media, mediaCaption, imageLoading, breadcrumb, }) => {
    const containerRef = React.useRef(null);
    useInsetReveal(containerRef, '[data-media-wrapper]');
    useGSAP(() => {
        if (isHeadlessBrowser()) {
            return;
        }
        gsap.fromTo('[data-fade]', {
            opacity: 0,
        }, {
            opacity: 1,
            duration: 2,
            ease: 'power3.out',
            stagger(index, target) {
                if (target instanceof HTMLElement && target.dataset.fade) {
                    const order = parseInt(target.dataset.fade);
                    if (!Number.isNaN(order)) {
                        return order * 0.2;
                    }
                }
                return index * 0.2;
            },
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 80%',
                end: 'top 20%',
                toggleActions: 'play none none none',
            },
        });
        gsap.fromTo('[data-stagger]', {
            opacity: 0,
            y: 20,
        }, {
            opacity: 1,
            y: 0,
            duration: 2,
            ease: 'power3.out',
            stagger(index, target) {
                if (target instanceof HTMLElement && target.dataset.stagger) {
                    const order = parseInt(target.dataset.stagger);
                    if (!Number.isNaN(order)) {
                        return order * 0.2;
                    }
                }
                return index * 0.2;
            },
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 80%',
                end: 'top 20%',
                toggleActions: 'play none none none',
            },
        });
    }, { scope: containerRef, dependencies: [isHeadlessBrowser] });
    const hasBreadcrumb = (breadcrumb?.links?.length ?? 0) > 0;
    return (_jsxs(SectionWrapper, { ref: containerRef, theme: theme, background: background, bottomPadding: bottomPadding, "data-test-id": "ArticleHero-organism", "data-epi-block-id": componentID, width: "full", className: "overflow-hidden", children: [hasBreadcrumb && (_jsx("div", { className: "site-container mt-[--headerInitialHeight]", children: _jsx(Breadcrumb, { ...breadcrumb }) })), _jsxs("article", { className: twMerge(getComponentPadding({ topPadding }), !hasBreadcrumb && 'mt-[--headerInitialHeight]', 'mx-auto flex max-w-[872px] flex-col items-center gap-1.5 px-1.5 text-center lg:gap-2'), "data-show-after-hydration": true, "data-stagger": "0", children: [_jsxs("div", { className: "flex flex-col gap-1.5 pt-2.5", children: [_jsxs("div", { className: "flex flex-row items-center justify-center gap-1 text-s-heading-sm tracking-10 text-text-body lg:gap-1.5", children: [category && _jsx("p", { className: "uppercase tracking-10", children: category }), category && articleDate && _jsx("span", { className: "text-text-placeholder", children: "|" }), articleDate && _jsx("time", { dateTime: articleDate, children: articleDate })] }), title && (_jsx(Heading, { variant: "ex-lg", "data-test-id": "title", "data-epi-property-name": "title", as: "h1", className: "leading-[1.2em]", children: emphasiseText(title) }))] }), text && _jsx("p", { className: "type-body-small text-text-body", children: text }), _jsxs("div", { className: "flex flex-col items-center gap-0.25 text-text-body", children: [_jsx("p", { className: "text-s-heading-sm uppercase tracking-10", children: author }), _jsx("p", { className: "type-body-md mb-3 font-bold", children: authorTitle })] })] }), media && (_jsx("div", { "data-show-after-hydration": true, "data-fade": "2", "data-media-wrapper": true, children: _jsx(Media, { containerClassName: "w-full h-full", mediaClassName: "aspect-square lg:aspect-[16/9]", imageClassName: "h-full aspect-square lg:aspect-[16/9]", imagePositioning: "none", media: media, caption: mediaCaption, imageLoading: imageLoading || 'eager', imageSizes: { base: { ratio: 1, vw: 1.2 }, lg: { ratio: 16 / 9 } }, captionClassName: "text-text-body italic pl-1.5 pt-[12vw] lg:pt-[calc(16/9*4vw)] md:pl-6" }) }))] }));
};
export default ArticleHero;
